import React, { useState } from "react";
import { any, isEmpty } from "ramda";
import { createSelector } from "reselect";

// TODO - import action creators for menu items
import { connect } from "react-redux";

import {
  saveTemplate as saveTemplateAction,
  openTemplate as openTemplateAction,
  deleteTemplate as deleteTemplateAction,
} from "actions/templates";
import Menu, { ISpilloverOptionProps } from "components/shared/spillover_menu";
import ManageTemplates from "components/templates/manage_templates";
import OpenTemplate from "components/templates/open_template";
import SaveTemplate from "components/templates/save_template";
import { printSchedule, pdfSchedule } from "helpers/schedule/print_pdf";
import { selectScheduling } from "store/select/scheduling";
import { IRootStateProps } from "types/reducers";
import { IPerson } from "types/schedules";
import { trackEvent } from "helpers/analytics";

interface IScheduleMenuProps extends IMappedStateProps, IMapDispatchToProps {
  publicURL: string;
}

enum TEMPLATE_MODALS {
  SAVE,
  OPEN,
  MANAGE,
}

function ScheduleMenu({
  isPublished,
  isSchedulePopulated,
  publicURL,
  requestStatuses,
  templates,
  saveTemplate,
  openTemplate,
  deleteTemplate,
}: IScheduleMenuProps) {
  const [modalKey, showModal] = useState<TEMPLATE_MODALS | null>(null);
  const [printWait, setPrintWait] = useState<boolean>(false);
  const [pdfWait, setPdfWait] = useState<boolean>(false);

  const optionGroups: ISpilloverOptionProps[][] = [
    [
      {
        children: "Save as Template",
        onClick: () => showModal(TEMPLATE_MODALS.SAVE),
      },
      {
        children: "Open Template",
        onClick: () => showModal(TEMPLATE_MODALS.OPEN),
        disabled: isEmpty(templates),
      },
      {
        children: "Manage Templates",
        onClick: () => {
          trackEvent("open manage templates modal");
          showModal(TEMPLATE_MODALS.MANAGE);
        },
        disabled: isEmpty(templates),
      },
    ],
    [
      {
        children: printWait
          ? "Printing Schedule..."
          : "Print Published Schedule",
        onClick: () => {
          setPrintWait(true);
          printSchedule(publicURL).then(() => setPrintWait(false));
        },
        disabled: !isPublished || printWait,
      },
      {
        children: pdfWait
          ? "Downloading Schedule..."
          : "Download Published Schedule (PDF)",
        onClick: () => {
          setPdfWait(true);
          pdfSchedule(publicURL).then(() => setPdfWait(false));
        },
        disabled: !isPublished || pdfWait,
      },
    ],
  ];

  let modal;
  if (modalKey === TEMPLATE_MODALS.SAVE) {
    modal = (
      <SaveTemplate
        onClose={() => showModal(null)}
        saveTemplate={saveTemplate}
        status={requestStatuses.postTemplate}
        templates={templates}
      />
    );
  } else if (modalKey === TEMPLATE_MODALS.OPEN) {
    modal = (
      <OpenTemplate
        onClose={() => showModal(null)}
        isSchedulePopulated={isSchedulePopulated}
        openTemplate={openTemplate}
        templates={templates}
      />
    );
  } else if (modalKey === TEMPLATE_MODALS.MANAGE) {
    modal = (
      <ManageTemplates
        deleteTemplate={deleteTemplate}
        onClose={() => showModal(null)}
        status={requestStatuses.postTemplate}
        templates={templates}
      />
    );
  }

  return (
    <>
      <Menu optionGroups={optionGroups} />
      {modal}
    </>
  );
}

interface IMappedStateProps {
  templates: IRootStateProps["templates"]["templates"];
  requestStatuses: IRootStateProps["templates"]["requestStatuses"];
  isPublished: boolean;
  isSchedulePopulated: boolean;
}

const anyShifts = any((person: IPerson) => person.shifts.length > 0);

const mapStateToPropsSelector = createSelector(
  selectScheduling,
  (state: IRootStateProps) => state.templates,
  ({ schedule, published }, templates): IMappedStateProps => {
    return {
      ...templates,
      isPublished: published.isJust(),
      isSchedulePopulated: anyShifts(schedule.people),
    };
  }
);

interface IMapDispatchToProps {
  saveTemplate: (location: string, name: string, uuid: string) => void;
  openTemplate: (templateId: string) => void;
  deleteTemplate: (location: string, uuid: string) => void;
}
const mapDispatchToProps: IMapDispatchToProps = {
  saveTemplate: saveTemplateAction,
  openTemplate: openTemplateAction,
  deleteTemplate: deleteTemplateAction,
};

// TODO - Connect mapDispatchToProps once action creators are built
export default connect(
  mapStateToPropsSelector,
  mapDispatchToProps
)(ScheduleMenu);
